/**
 * usePageContext Hook
 *
 * Detects the current page context to pre-populate AI colleague conversations.
 * Extracts entity type and ID from the current URL.
 * Uses the current tab name for a friendly display.
 */
import { useMemo } from 'react';
import { useLocation } from '@/hooks/useLocation';
import { useTabsContext } from '@/context/TabsContext';
import type { AIColleagueContext, ColleagueId, ContextType } from './useAIColleague';

type PageContextResult = {
  /** Detected context from URL */
  context: AIColleagueContext | undefined;
  /** Suggested colleague based on current page */
  suggestedColleague: ColleagueId;
  /** Human-readable description of context */
  contextDescription: string | undefined;
  /** Current tab title (friendly name) */
  tabTitle: string | undefined;
};

const DASHBOARD_QUEUE_ROUTES = {
  pendingPayments: {
    label: 'Pending Payments',
    path: '/pending-instructions/payments',
    domain: 'Fiat Operations'
  },
  pendingInstructions: {
    label: 'Pending Instructions',
    path: '/pending-instructions/instructions',
    domain: 'Fiat Operations'
  },
  cashInOut: {
    label: 'Cash In/Out',
    path: '/instructions/cash-in-out',
    domain: 'Fiat Operations'
  },
  depositExceptions: {
    label: 'Deposit Exceptions',
    path: '/digital-custody/deposits/pending',
    domain: 'Digital Assets'
  },
  rejectedDeposits: {
    label: 'Rejected Deposits',
    path: '/digital-custody/deposits',
    domain: 'Digital Assets'
  },
  withdrawalRequests: {
    label: 'Withdrawal Requests',
    path: '/digital-custody/withdraws',
    domain: 'Digital Assets'
  },
  cardApplications: {
    label: 'Card Applications',
    path: '/cms/card-application',
    domain: 'Cards'
  },
  pendingEmbossedCards: {
    label: 'Pending Embossed',
    path: '/cms/card-pending-embossed',
    domain: 'Cards'
  },
  solutionApplications: {
    label: 'Solution Applications',
    path: '/solution-application',
    domain: 'Onboarding'
  },
  craForms: {
    label: 'CRA Forms',
    path: '/pending-instructions/cra-form',
    domain: 'Onboarding'
  },
  earnSubscriptions: {
    label: 'Earn Subscriptions',
    path: '/earn/subscriptions',
    domain: 'Investments'
  },
  fundInstructions: {
    label: 'Fund Instructions',
    path: '/fund/instructions',
    domain: 'Investments'
  },
  securitiesTransfers: {
    label: 'Securities Transfers',
    path: '/pending-instructions/instructions?intent=SECURITIES_TRANSFER',
    domain: 'Investments'
  },
  preciousMetals: {
    label: 'Precious Metals',
    path: '/precious-metal/instructions',
    domain: 'Physical Assets'
  }
} as const;

const buildDashboardQueueContext = (data?: Record<string, unknown>): Record<string, unknown> => {
  const queue = data
    ? {
        pendingPayments: data.payments,
        pendingInstructions: data.instructions,
        depositExceptions: data.digitalDepositPending,
        rejectedDeposits: data.digitalDepositRejected,
        withdrawalRequests: data.digitalWithdrawPending,
        cardApplications: data.cardApplicationCount,
        pendingEmbossedCards: data.cardPendingEmbossedCount,
        solutionApplications: data.solutionApplications,
        craForms: data.craFormInstructions,
        cashInOut: data.cashInOutPending,
        earnSubscriptions: data.earnSubscriptionsPending,
        fundInstructions: data.fundInstructionsPending,
        securitiesTransfers: data.securitiesTransfersPending,
        preciousMetals: data.preciousMetalPending
      }
    : {};

  return {
    page: 'Dashboard',
    domain: 'operations_work_queue',
    queueSnapshotAvailable: Boolean(data),
    queue,
    queueRoutes: DASHBOARD_QUEUE_ROUTES,
    queueActions: Object.fromEntries(
      Object.entries(DASHBOARD_QUEUE_ROUTES).map(([key, route]) => [
        key,
        {
          label: 'Open queue',
          action: 'open_queue',
          actionType: 'internal_route',
          route: route.path,
          canonicalRoute: route.path,
          target: 'ops-web',
          kind: 'queue',
          queueLabel: route.label,
          domain: route.domain
        }
      ])
    ),
    guidance:
      'Use queue counts together with queueActions/queueRoutes. When answering operator triage or navigation questions, name the queue label and render an Open queue markdown link with the exact canonicalRoute.'
  };
};

const readDashboardQueueContext = (): Record<string, unknown> | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const raw = window.localStorage.getItem('dashboard-latest-data');
    if (!raw) {
      return buildDashboardQueueContext();
    }

    const data = JSON.parse(raw) as Record<string, unknown>;
    return buildDashboardQueueContext(data);
  } catch {
    return buildDashboardQueueContext();
  }
};

const readWhitelistedSearchParams = (search: string | undefined, allowedKeys: string[]): Record<string, string> => {
  if (!search) {
    return {};
  }

  const allowed = new Set(allowedKeys);
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    if (value && allowed.has(key)) {
      result[key] = value;
    }
  });

  return result;
};

/**
 * Route patterns and their context mappings
 */
const ROUTE_PATTERNS: Array<{
  pattern: RegExp;
  contextType: ContextType;
  idExtractor: (matches: RegExpMatchArray) => string;
  dataExtractor?: (matches: RegExpMatchArray) => Record<string, unknown>;
  colleague: ColleagueId;
  description: (id: string) => string;
}> = [
  // Digital custody deposit details
  {
    pattern: /\/digital-custody\/deposits\/([a-f0-9-]{36})/i,
    contextType: 'digitalDeposit',
    idExtractor: (m) => m[1],
    dataExtractor: (m) => ({
      depositId: m[1],
      domain: 'digital_custody',
      operationType: 'digital_deposit',
      expectedReferencePrefix: 'DD',
      grounding:
        'This route is a digital deposit detail page. Do not reinterpret this record as a fiat withdrawal.'
    }),
    colleague: 'morgan',
    description: (id) => `Digital deposit ${id.slice(0, 8)}...`
  },
  // Digital custody withdrawal details
  {
    pattern: /\/digital-custody\/withdraws\/([a-f0-9-]{36})/i,
    contextType: 'digitalWithdraw',
    idExtractor: (m) => m[1],
    dataExtractor: (m) => ({
      withdrawId: m[1],
      domain: 'digital_custody',
      operationType: 'digital_withdrawal',
      expectedReferencePrefix: 'DW',
      grounding:
        'This route is a digital withdrawal detail page. Do not reinterpret this record as a fiat deposit or fiat withdrawal.'
    }),
    colleague: 'morgan',
    description: (id) => `Digital withdraw ${id.slice(0, 8)}...`
  },
  // Card details
  {
    pattern: /\/cms\/cards\/([a-f0-9-]{36})\/([a-f0-9-]{36})/i,
    contextType: 'card',
    idExtractor: (m) => m[1],
    dataExtractor: (m) => ({
      cardId: m[1],
      userId: m[2],
      domain: 'cards'
    }),
    colleague: 'casey',
    description: (id) => `Card ${id.slice(0, 8)}...`
  },
  // Compliance cases
  {
    pattern: /\/compliance\/cases?\/(CC-[\d]+|[a-f0-9-]{36})/i,
    contextType: 'case',
    idExtractor: (m) => m[1],
    colleague: 'alex',
    description: (id) => `Case ${id}`
  },
  // KYT Screenings
  {
    pattern: /\/(?:digital\/screening|digital-custody\/screening-history|compliance\/kyt-screening)\/([a-f0-9-]{36})/i,
    contextType: 'screening',
    idExtractor: (m) => m[1],
    colleague: 'morgan',
    description: (id) => `Screening ${id.slice(0, 8)}...`
  },
  // Fiat instructions
  {
    pattern: /\/(?:fiat|deposit|withdraw).*\/(GI[\d]+|[a-f0-9-]{36})/i,
    contextType: 'instruction',
    idExtractor: (m) => m[1],
    colleague: 'jordan',
    description: (id) => `Instruction ${id}`
  },
  // Digital instructions
  {
    pattern: /\/digital\/(?:deposit|withdraw)\/([a-f0-9-]{36})/i,
    contextType: 'instruction',
    idExtractor: (m) => m[1],
    colleague: 'jordan',
    description: (id) => `Digital instruction ${id.slice(0, 8)}...`
  },
  // Support tickets
  {
    pattern: /\/support(?:-center)?\/(?:ticket|all-requests|my-requests|team-requests)\/(\d+|[a-f0-9-]{36})/i,
    contextType: 'ticket',
    idExtractor: (m) => m[1],
    colleague: 'sam',
    description: (id) => `Ticket #${id}`
  },
  // User profiles
  {
    pattern: /\/(?:user|customer|client)s?\/([a-f0-9-]{36})/i,
    contextType: 'user',
    idExtractor: (m) => m[1],
    colleague: 'riley',
    description: (id) => `User ${id.slice(0, 8)}...`
  },
  // Solution applications
  {
    pattern: /\/solutions?\/(?:application|apply)\/([a-f0-9-]{36})/i,
    contextType: 'solution',
    idExtractor: (m) => m[1],
    colleague: 'taylor',
    description: (id) => `Solution application ${id.slice(0, 8)}...`
  }
];

const CONTEXT_PAGE_PATTERNS: Array<{
  pattern: RegExp;
  contextType: ContextType;
  id: string;
  colleague: ColleagueId;
  description: string;
  data?: (search: string | undefined) => Record<string, unknown> | undefined;
}> = [
  {
    pattern: /^\/cost-and-fees\/cost-management\/?$/i,
    contextType: 'feesPricing',
    id: 'cost-management',
    colleague: 'jordan',
    description: 'Cost Management',
    data: (search) => ({
      page: 'Cost Management',
      domain: 'fees_pricing',
      operationType: 'fee_cost_management',
      activeFilters: readWhitelistedSearchParams(search, ['feeSettingName', 'feeSettingType', 'currency', 'intent'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/fee-markup-management\/?$/i,
    contextType: 'feesPricing',
    id: 'fee-markup-management',
    colleague: 'jordan',
    description: 'Markup Management',
    data: (search) => ({
      page: 'Markup Management',
      domain: 'fees_pricing',
      operationType: 'fee_markup_management',
      activeFilters: readWhitelistedSearchParams(search, ['feeSettingName', 'feeSettingType', 'currency', 'intent'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/fee-schedule\/?$/i,
    contextType: 'feesPricing',
    id: 'fee-schedule',
    colleague: 'jordan',
    description: 'Fee Schedule',
    data: (search) => ({
      page: 'Fee Schedule',
      domain: 'fees_pricing',
      operationType: 'fee_schedule',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'userGroupId', 'intent', 'category'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/fee-schedule-v2\/?$/i,
    contextType: 'feesPricing',
    id: 'fee-schedule-v2',
    colleague: 'jordan',
    description: 'New Fee Schedule',
    data: (search) => ({
      page: 'New Fee Schedule',
      domain: 'fees_pricing',
      operationType: 'fee_schedule_v2',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'userGroupId', 'intent', 'category'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/users-fee-schedule\/?$/i,
    contextType: 'feesPricing',
    id: 'users-fee-schedule',
    colleague: 'jordan',
    description: 'Users Fee Schedule',
    data: (search) => ({
      page: 'Users Fee Schedule',
      domain: 'fees_pricing',
      operationType: 'users_fee_schedule',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'userGroupId', 'accountId', 'email', 'intent'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/cobrands-fee-schedule(?:\/[^/]+)?\/?$/i,
    contextType: 'feesPricing',
    id: 'cobrands-fee-schedule',
    colleague: 'jordan',
    description: 'Cobrands Fee Schedule',
    data: (search) => ({
      page: 'Cobrands Fee Schedule',
      domain: 'fees_pricing',
      operationType: 'cobrand_fee_schedule',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'cobrandId', 'intent', 'category'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/commission-management\/?$/i,
    contextType: 'feesPricing',
    id: 'commission-management',
    colleague: 'jordan',
    description: 'Commission Management',
    data: (search) => ({
      page: 'Commission Management',
      domain: 'fees_pricing',
      operationType: 'commission_management',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'status', 'accountId', 'currency'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/fee-setting-request\/?$/i,
    contextType: 'feesPricing',
    id: 'fee-setting-request',
    colleague: 'jordan',
    description: 'Fee Setting Requests',
    data: (search) => ({
      page: 'Fee Setting Requests',
      domain: 'fees_pricing',
      operationType: 'fee_setting_request',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'status', 'requestType', 'accountId'])
    })
  },
  {
    pattern: /^\/cost-and-fees\/agent-onboarding\/?$/i,
    contextType: 'feesPricing',
    id: 'agent-onboarding-fee-settings',
    colleague: 'jordan',
    description: 'Agent Onboarding Fee Settings',
    data: (search) => ({
      page: 'Agent Onboarding Fee Settings',
      domain: 'fees_pricing',
      operationType: 'agent_onboarding_fee_settings',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'agentId', 'status'])
    })
  },
  {
    pattern: /^\/user-groups(?:\/[^/]+)?\/?$/i,
    contextType: 'feesPricing',
    id: 'user-groups',
    colleague: 'jordan',
    description: 'User Groups',
    data: (search) => ({
      page: 'User Groups',
      domain: 'fees_pricing',
      operationType: 'user_group_fee_schedule_scope',
      activeFilters: readWhitelistedSearchParams(search, ['vendorId', 'groupId', 'name'])
    })
  },
  {
    pattern: /^\/dashboard\/?$/i,
    contextType: 'dashboard',
    id: 'dashboard',
    colleague: 'riley',
    description: 'Dashboard work queue',
    data: readDashboardQueueContext
  },
  {
    pattern: /^\/cms\/cards\/?$/i,
    contextType: 'cardList',
    id: 'cards-list',
    colleague: 'casey',
    description: 'Cards search results',
    data: (search) => ({
      page: 'Cards',
      operationType: 'card_search',
      lookupKeys: ['card last four digits', 'cardholder name', 'card account', 'card product'],
      activeFilters: readWhitelistedSearchParams(search, [
        'last4',
        'cardLast4',
        'cardLastFour',
        'cardProductName',
        'productName',
        'cardAccount',
        'cardAccountNumber',
        'status'
      ])
    })
  },
  {
    pattern: /^\/cms\/card-application\/?$/i,
    contextType: 'cardList',
    id: 'card-applications',
    colleague: 'casey',
    description: 'Card Applications',
    data: (search) => ({
      page: 'Card Applications',
      operationType: 'card_application',
      lookupKeys: ['application status', 'card last four digits', 'embossed name', 'card product', 'card account'],
      activeFilters: readWhitelistedSearchParams(search, [
        'status',
        'last4',
        'cardLast4',
        'cardLastFour',
        'cardEmbossedName',
        'embossedName',
        'cardProductName',
        'productName',
        'cardAccount',
        'cardAccountNumber',
        'cardAccountLabel'
      ]),
      guidance:
        'This page is a card application queue. Card last4 can be duplicated; verify with embossed name, card product, card account, or client account before treating a match as definitive.'
    })
  },
  {
    pattern: /^\/cms\/card-pending-embossed\/?$/i,
    contextType: 'cardList',
    id: 'pending-embossed-cards',
    colleague: 'casey',
    description: 'Pending Embossed Cards',
    data: (search) => ({
      page: 'Pending Embossed Cards',
      operationType: 'pending_embossed_cards',
      lookupKeys: ['card last four digits', 'embossed name', 'card product', 'card account'],
      activeFilters: readWhitelistedSearchParams(search, [
        'last4',
        'cardLast4',
        'cardLastFour',
        'cardEmbossedName',
        'embossedName',
        'cardProductName',
        'productName',
        'cardProgram',
        'cardAccount',
        'cardAccountNumber',
        'cardAccountLabel',
        'status'
      ]),
      guidance:
        'This page is the pending embossed card queue. Do not request or repeat full card numbers; use last4 plus embossed name or account details for verification.'
    })
  },
  {
    pattern: /^\/cms\/card-disputes\/?$/i,
    contextType: 'cardList',
    id: 'card-disputes',
    colleague: 'casey',
    description: 'Card Disputes',
    data: (search) => ({
      page: 'Card Disputes',
      operationType: 'card_disputes',
      lookupKeys: ['transaction reference', 'dispute status', 'card last four digits', 'embossed name', 'card product'],
      activeFilters: readWhitelistedSearchParams(search, [
        'status',
        'ref',
        'refId',
        'transactionRef',
        'last4',
        'cardLast4',
        'cardLastFour',
        'cardEmbossedName',
        'embossedName',
        'cardProductName',
        'productName',
        'cardAccountNumber',
        'cardAccountLabel'
      ]),
      guidance:
        'This page is a card dispute queue. If last4 matches more than one card, verify the embossed name, card product, account, or client before answering definitively.'
    })
  },
  {
    pattern: /^\/digital-custody\/deposits\/pending\/?$/i,
    contextType: 'digitalDepositQueue',
    id: 'pending-digital-deposits',
    colleague: 'morgan',
    description: 'Pending Digital Deposits',
    data: (search) => ({
      page: 'Pending Digital Deposits',
      operationType: 'digital_deposit',
      expectedReferencePrefix: 'DD',
      activeFilters: readWhitelistedSearchParams(search, ['ref', 'reference', 'referenceId', 'status', 'asset', 'network']),
      guidance:
        'References on this page are digital deposit refs. Use digital deposit lookup, not fiat instruction lookup.'
    })
  },
  {
    pattern: /^\/digital-custody\/deposits\/?$/i,
    contextType: 'digitalDepositQueue',
    id: 'digital-deposits',
    colleague: 'morgan',
    description: 'Digital Deposits',
    data: (search) => ({
      page: 'Digital Deposits',
      operationType: 'digital_deposit',
      expectedReferencePrefix: 'DD',
      activeFilters: readWhitelistedSearchParams(search, ['ref', 'reference', 'referenceId', 'status', 'asset', 'network'])
    })
  },
  {
    pattern: /^\/digital-custody\/withdraws\/?$/i,
    contextType: 'digitalWithdrawQueue',
    id: 'digital-withdrawals',
    colleague: 'morgan',
    description: 'Digital Withdrawals',
    data: (search) => ({
      page: 'Digital Withdrawals',
      operationType: 'digital_withdrawal',
      expectedReferencePrefix: 'DW',
      activeFilters: readWhitelistedSearchParams(search, ['ref', 'reference', 'referenceId', 'status', 'asset', 'network'])
    })
  }
];

/**
 * Page-level context (no specific entity)
 */
const PAGE_PATTERNS: Array<{
  pattern: RegExp;
  colleague: ColleagueId;
}> = [
  { pattern: /\/compliance/i, colleague: 'alex' },
  { pattern: /\/digital\/screening|\/digital-custody\/screening-history|\/compliance\/kyt-screening/i, colleague: 'morgan' },
  { pattern: /\/digital-custody|\/digital\/custody|\/wallet/i, colleague: 'morgan' },
  { pattern: /\/fiat|\/deposit|\/withdraw|\/payment/i, colleague: 'jordan' },
  { pattern: /\/cost-and-fees|\/user-groups/i, colleague: 'jordan' },
  { pattern: /\/support|\/ticket/i, colleague: 'sam' },
  { pattern: /\/solution|\/trust|\/fund|\/insurance/i, colleague: 'taylor' },
  { pattern: /\/card|\/cms|\/issuing|\/emboss/i, colleague: 'casey' }
];

/**
 * Hook to detect page context from current URL
 */
export function usePageContext(): PageContextResult {
  const location = useLocation();
  const { activeTab } = useTabsContext();
  const tabTitle = activeTab?.title;
  const dashboardQueueSnapshot =
    typeof window !== 'undefined' ? window.localStorage.getItem('dashboard-latest-data') : undefined;

  return useMemo(() => {
    const { pathname } = location;

    // Try to match entity-level patterns first
    for (const routeConfig of ROUTE_PATTERNS) {
      const match = pathname.match(routeConfig.pattern);
      if (match) {
        const id = routeConfig.idExtractor(match);
        return {
          context: {
            type: routeConfig.contextType,
            id,
            data: routeConfig.dataExtractor?.(match)
          },
          suggestedColleague: routeConfig.colleague,
          contextDescription: routeConfig.description(id),
          tabTitle
        };
      }
    }

    for (const pageConfig of CONTEXT_PAGE_PATTERNS) {
      if (pageConfig.pattern.test(pathname)) {
        return {
          context: {
            type: pageConfig.contextType,
            id: pageConfig.id,
            data: pageConfig.data?.(location.search)
          },
          suggestedColleague: pageConfig.colleague,
          contextDescription: pageConfig.description,
          tabTitle
        };
      }
    }

    // Try page-level patterns
    for (const pageConfig of PAGE_PATTERNS) {
      if (pageConfig.pattern.test(pathname)) {
        return {
          context: undefined,
          suggestedColleague: pageConfig.colleague,
          contextDescription: undefined,
          tabTitle
        };
      }
    }

    // Default to Riley (general ops)
    return {
      context: undefined,
      suggestedColleague: 'riley',
      contextDescription: undefined,
      tabTitle
    };
  }, [location.pathname, location.search, tabTitle, dashboardQueueSnapshot]);
}

export default usePageContext;
